import { useState } from 'react';
import { Box, Group, SimpleGrid, Stack, Switch, Text } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { TV_PREVIEW_GEOMETRY as G } from '@/lib/constants/appFactory';

interface TvDevicePreviewsProps {
  /** name → URL of the set's files (BrandKitUtils.previewFileMap). */
  files: Record<string, string>;
  /** Width of each TV frame in the preview, px (the frame is 16:9). */
  frameWidth?: number;
}

const FRAME_BG = '#0b0e14';
const PLACEHOLDER = '#2a2f3a';

/**
 * TV graphics v2 (design D5): the set's files drawn where each TV shows
 * them, at their on-screen proportions of a 1920 x 1080 frame — the Google TV
 * home row's circle icon, the Google TV Apps-page banner, the Fire TV home
 * row's tile (approximate size; the 882 x 448 content-safe area on demand),
 * the Google Play listing art, and the splash. Neutral placeholders beside
 * each tile give the scale a viewer sees next to other apps. Pure CSS: the
 * adaptive icon is the two 108 dp layers with the central 72 dp showing
 * through a circle, as the launcher masks it.
 */
export function TvDevicePreviews({ files, frameWidth = 560 }: TvDevicePreviewsProps) {
  const [safeArea, setSafeArea] = useState(false);
  const [focused, setFocused] = useState(true);
  const k = frameWidth / G.frameW; // frame px per 1080p px

  return (
    <Stack gap="lg">
      <Preview title="Google TV — Your apps" note="The home screen shows your app as a circle.">
        <TvFrame width={frameWidth}>
          <Group gap={Math.round(28 * k)} justify="center" wrap="nowrap">
            <Circle size={G.googleTvCirclePx * k} />
            <AdaptiveCircle files={files} size={G.googleTvCirclePx * k} />
            <Circle size={G.googleTvCirclePx * k} />
          </Group>
        </TvFrame>
        <Group gap="sm" mt={6} align="center">
          <Text size="xs" c="dimmed">
            Close-up:
          </Text>
          <AdaptiveCircle files={files} size={96} />
        </Group>
      </Preview>

      <Preview title="Google TV — Apps page" note="The wide banner in the list of installed apps.">
        <TvFrame width={frameWidth}>
          <Group gap={Math.round(24 * k)} justify="center" wrap="nowrap">
            <Tile w={G.googleTvBannerPx.w * k} h={G.googleTvBannerPx.h * k} />
            <Tile w={G.googleTvBannerPx.w * k} h={G.googleTvBannerPx.h * k} src={files['tv-banner.png']} alt="TV banner" />
            <Tile w={G.googleTvBannerPx.w * k} h={G.googleTvBannerPx.h * k} />
          </Group>
        </TvFrame>
      </Preview>

      <Preview title="Fire TV — Home row" note="The tile comes from your Amazon listing (the Store Icon).">
        <TvFrame width={frameWidth}>
          <Group gap={Math.round(24 * k)} justify="center" wrap="nowrap">
            <Tile w={G.fireTvTilePx.w * k} h={G.fireTvTilePx.h * k} />
            <Tile
              w={G.fireTvTilePx.w * k}
              h={G.fireTvTilePx.h * k}
              src={files['listing-icon.png']}
              alt="Fire TV tile"
              safeArea={safeArea}
              focused={focused}
            />
            <Tile w={G.fireTvTilePx.w * k} h={G.fireTvTilePx.h * k} />
          </Group>
        </TvFrame>
        <Group gap="md" mt={6}>
          <Switch size="xs" label="Show the safe area" checked={safeArea} onChange={(e) => setSafeArea(e.currentTarget.checked)} />
          <Switch size="xs" label="Focused" checked={focused} onChange={(e) => setFocused(e.currentTarget.checked)} />
        </Group>
      </Preview>

      <Preview title="Google Play listing" note="The icon, the TV banner and the feature graphic of the store page.">
        <SimpleGrid cols={3} spacing="sm">
          <Img src={files['icon.png']} alt="Store icon" style={{ aspectRatio: '1 / 1', borderRadius: '20%' }} />
          <Img src={files['listing-icon.png']} alt="TV banner" style={{ aspectRatio: '16 / 9' }} />
          <Img src={files['listing-feature-graphic.png']} alt="Feature graphic" style={{ aspectRatio: '1024 / 500' }} />
        </SimpleGrid>
      </Preview>

      <Preview title="Splash" note="What a viewer sees while the app opens.">
        <TvFrame width={frameWidth} ground={files['icon-background.png']}>
          {files['splash-logo.png'] ? (
            <img
              src={files['splash-logo.png']}
              alt="Splash logo"
              style={{ maxHeight: '40%', maxWidth: '50%', objectFit: 'contain' }}
            />
          ) : (
            <Missing />
          )}
        </TvFrame>
      </Preview>
    </Stack>
  );
}

// Shared with RokuScreenPreviews: a titled preview, a 16:9 TV frame, the missing-file mark.
export function Preview({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {title}
      </Text>
      <Text size="xs" c="dimmed">
        {note}
      </Text>
      {children}
    </Stack>
  );
}

export function TvFrame({ width, ground, children }: { width: number; ground?: string; children: ReactNode }) {
  return (
    <Box
      data-testid="tv-frame"
      style={{
        width,
        maxWidth: '100%',
        aspectRatio: '16 / 9',
        background: ground ? `url(${ground}) center / cover no-repeat` : FRAME_BG,
        borderRadius: 8,
        border: '6px solid #1c1f26',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
}

function Circle({ size }: { size: number }) {
  return <Box style={{ width: size, height: size, borderRadius: '50%', background: PLACEHOLDER, flex: 'none' }} />;
}

/**
 * The adaptive icon as the launcher draws it: both 108 dp layers at
 * size / (72/108), centred, clipped to a circle of `size`. Without the two
 * layers (a set from before composer v2) the old square icon stands in.
 */
function AdaptiveCircle({ files, size }: { files: Record<string, string>; size: number }) {
  const fg = files['icon-foreground.png'];
  const bg = files['icon-background.png'];
  const layer = size / G.adaptiveVisibleFrac;
  const off = (size - layer) / 2;
  const layerStyle: CSSProperties = { position: 'absolute', left: off, top: off, width: layer, height: layer };
  return (
    <Box
      data-testid="google-tv-circle"
      style={{ position: 'relative', width: size, height: size, borderRadius: '50%', overflow: 'hidden', flex: 'none', background: '#ffffff' }}
    >
      {fg ? (
        <>
          {bg && <img src={bg} alt="" style={layerStyle} />}
          <img src={fg} alt="Google TV icon" style={layerStyle} />
        </>
      ) : files['icon.png'] ? (
        <img src={files['icon.png']} alt="Google TV icon (from the app icon)" style={{ ...layerStyle, left: size * 0.17, top: size * 0.17, width: size * 0.66, height: size * 0.66 }} />
      ) : null}
    </Box>
  );
}

function Tile({ w, h, src, alt, safeArea, focused }: { w: number; h: number; src?: string; alt?: string; safeArea?: boolean; focused?: boolean }) {
  const sa = G.fireTvSafeArea;
  return (
    <Box
      style={{
        position: 'relative',
        width: w,
        height: h,
        flex: 'none',
        borderRadius: 4,
        overflow: 'hidden',
        background: PLACEHOLDER,
        transform: focused ? 'scale(1.1)' : undefined,
        boxShadow: focused ? '0 0 0 2px #ffffff' : undefined,
      }}
    >
      {src ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : alt ? <Missing /> : null}
      {safeArea && (
        <Box
          data-testid="fire-tv-safe-area"
          style={{
            position: 'absolute',
            left: `${((1 - sa.w / sa.of.w) / 2) * 100}%`,
            top: `${((1 - sa.h / sa.of.h) / 2) * 100}%`,
            width: `${(sa.w / sa.of.w) * 100}%`,
            height: `${(sa.h / sa.of.h) * 100}%`,
            border: '1px dashed #ffd400',
          }}
        />
      )}
    </Box>
  );
}

function Img({ src, alt, style }: { src?: string; alt: string; style: CSSProperties }) {
  return src ? (
    <img src={src} alt={alt} style={{ width: '100%', objectFit: 'cover', background: PLACEHOLDER, ...style }} />
  ) : (
    <Box style={{ ...style, width: '100%', background: PLACEHOLDER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Missing />
    </Box>
  );
}

export function Missing() {
  return (
    <Text size="xs" c="dimmed">
      not in this set
    </Text>
  );
}
